import React, { ReactNode, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { ModalsKeys, Nullable, SelectOption } from 'global';
import { DialogHeader } from 'components/shared/Dialog/DialogHeader';
import { Button, Dialog, DialogContent, Stack, ButtonGroup } from '@mui/material';

import { ActivityType } from '../../types';
import { useDealActivityMutations } from '../../api';
import { CallActivityForm } from '../form/CallActivityForm';
import { NoteActivityForm } from '../form/NoteActivityForm';

export const ActivityDialog = () => {
  const { handleClose, isOpen, data } = useModal(ModalsKeys.ActivityDialog);

  const [currentActivityType, setCurrentActivityType] = useState<ActivityType>(
    data?.initialTab === 'NOTE' ? ActivityType.NOTE : ActivityType.CALL
  );

  const { handleCreateDealActivity, isDealActivityCreating } = useDealActivityMutations();

  const handleSubmitCall = async (formValues: {
    status: string;
    subject: string;
    direction: string;
    description: string;
    contact: Nullable<SelectOption>;
  }) => {
    const commonVariables = { type: ActivityType.CALL, parentType: data.parentType, parentId: data.parentId };

    await handleCreateDealActivity({
      ...commonVariables,
      call: {
        status: formValues.status,
        subject: formValues.subject,
        callDirection: formValues.direction,
        description: formValues.description,
        contactId: formValues.contact!.value,
      },
    });

    handleClose();
  };

  const handleSubmitNote = async (formValues: { content: string; subject: string }) => {
    const commonVariables = {
      type: ActivityType.NOTE,
      parentId: data.parentId,
      parentType: data.parentType,
    };

    await handleCreateDealActivity({ ...commonVariables, note: { contactIds: [], ...formValues } });

    handleClose();
  };

  const renderByType: Record<ActivityType, ReactNode> = {
    [ActivityType.CALL]: (
      <CallActivityForm
        isEdit={false}
        initialValues={null}
        onCancel={handleClose}
        onSubmit={handleSubmitCall}
        isSubmitting={isDealActivityCreating}
      />
    ),
    [ActivityType.NOTE]: (
      <NoteActivityForm
        isEdit={false}
        initialValues={null}
        onCancel={handleClose}
        onSubmit={handleSubmitNote}
        isSubmitting={isDealActivityCreating}
      />
    ),
    [ActivityType.EMAIL]: null,
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogHeader label="Add New" handleClose={handleClose} />

      <DialogContent>
        <Stack gap="1rem" sx={{ paddingTop: '0.5rem' }}>
          <ButtonGroup color="primary" fullWidth>
            {[
              { label: 'Call', value: ActivityType.CALL },
              { label: 'Note', value: ActivityType.NOTE },
            ].map(({ label, value }) => (
              <Button
                key={value}
                variant={currentActivityType === value ? 'contained' : 'outlined'}
                onClick={() => {
                  if (currentActivityType === value) return;
                  setCurrentActivityType(value);
                }}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>

          {renderByType[currentActivityType]}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
